<script setup lang="ts">
/**
 * Decision 18 — "We've updated our terms" for a signed-in shopper whose accepted versions are
 * not the ones in force (`GET /consents/current` → `mustAccept`). Shown while the texts are
 * drafts too (`is_placeholder`, with a line saying so). No Figma frame (design gap): the cookie
 * banner's layout and classes (Figma `Cookies` 39:41637). Waits until the cookie banner is gone
 * so the two never stack. 409 on accept = a version moved: re-read and ask again.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useTokenStore } from '@/features/auth/store'
import { hasProfileApi } from '@/features/profile/flags'
import { useFairCookieConsent } from '@/features/cookie-consent/store/useFairCookieConsentStore'
import { useConsentsCurrentQuery } from '@/queries/account'
import { acceptConsents } from '../api/account'
import { consentsAcceptRequest, consentsToAccept, isStaleConsentVersion } from '../consents.data'
import '@/features/cookie-consent/ui/fair-cookie-banner.css'

const { t } = useI18n()
const toast = useFairToast()
const tokenStore = useTokenStore()
const { bannerVisible: cookieBannerVisible } = storeToRefs(useFairCookieConsent())

const signedIn = computed(() => Boolean(tokenStore.accessToken))
const query = useConsentsCurrentQuery(signedIn, computed(() => signedIn.value && hasProfileApi('consents')))

const toAccept = computed(() => consentsToAccept(query.data.value))
const accepting = ref(false)
const visible = computed(() => signedIn.value && !cookieBannerVisible.value && toAccept.value.length > 0)
const isDraft = computed(() => toAccept.value.some((doc) => doc.isPlaceholder))

async function accept() {
  if (accepting.value) return
  accepting.value = true
  try {
    const next = await acceptConsents(consentsAcceptRequest(toAccept.value, 'banner'))
    query.setData(next)
  } catch (error) {
    if (isStaleConsentVersion(error)) {
      await query.refresh(true)
    } else {
      console.warn('Consents: not accepted', error)
      toast.show({ tone: 'warning', message: t('account.consents.accept_failed') })
    }
  } finally {
    accepting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-cookie-banner">
      <section
        v-if="visible"
        class="fair-cookie-banner"
        role="dialog"
        aria-modal="false"
        :aria-label="t('account.consents.aria_label')"
        data-testid="fair-consents-banner"
      >
        <div class="fair-cookie-banner__content">
          <p class="fair-cookie-banner__text">
            <i18n-t
              keypath="account.consents.text"
              tag="span"
              scope="global"
            >
              <template #documents>
                <template
                  v-for="(doc, index) in toAccept"
                  :key="doc.kind"
                >
                  <template v-if="index > 0">
                    {{ index === toAccept.length - 1 ? ` ${t('account.consents.and')} ` : ', ' }}
                  </template>
                  <a
                    v-if="doc.bodyUrl"
                    class="fair-cookie-banner__policy"
                    :href="doc.bodyUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ t(`account.consents.kinds.${doc.kind}`) }}</a>
                  <span v-else>{{ t(`account.consents.kinds.${doc.kind}`) }}</span>
                </template>
              </template>
            </i18n-t>
            <template v-if="isDraft">
              {{ ' ' + t('account.consents.draft') }}
            </template>
          </p>
        </div>
        <div class="fair-cookie-banner__footer">
          <FairButton
            class="fair-cookie-banner__button"
            variant="primary"
            size="auto"
            :label="t('account.consents.accept')"
            :loading="accepting"
            data-testid="fair-consents-accept"
            @click="accept"
          />
        </div>
      </section>
    </Transition>
  </Teleport>
</template>
